/**
 * Composio Auth Setup Card
 *
 * Shared payload shape for inline Losi auth cards when a Connected Services
 * toolkit needs the user to connect (SoftGate / tool-result UI).
 *
 * Primary responsibilities:
 * - Build a stable setupCard object for chat tool results
 * - Keep display names readable for common toolkit slugs
 *
 * Dependencies:
 * - None (pure helpers)
 */

export type ComposioAuthSetupCard = {
  kind: 'composio_auth'
  title: string
  description: string
  toolkit_slug: string
  connection_url: string | null
  action_label: string
  logo?: string
}

const DISPLAY_NAMES: Record<string, string> = {
  gmail: 'Gmail',
  github: 'GitHub',
  googlecalendar: 'Google Calendar',
  googlesheets: 'Google Sheets',
  twitter: 'X (Twitter)',
  notion: 'Notion',
  googledrive: 'Google Drive',
  slack: 'Slack',
  outlook: 'Outlook',
  hubspot: 'HubSpot',
  googledocs: 'Google Docs',
  jira: 'Jira',
  youtube: 'YouTube',
  asana: 'Asana',
  googletasks: 'Google Tasks',
  clickup: 'ClickUp',
  reddit: 'Reddit',
  linkedin: 'LinkedIn',
  microsoftteams: 'Microsoft Teams',
  calendly: 'Calendly',
  zoom: 'Zoom',
  googlemeet: 'Google Meet',
  onedrive: 'OneDrive',
  dropbox: 'Dropbox',
  posthog: 'PostHog'
}

export function getComposioToolkitDisplayName(slug: string): string {
  const normalized = slug.trim().toLowerCase()
  if (!normalized) return 'Account'
  return (
    DISPLAY_NAMES[normalized] ||
    normalized.charAt(0).toUpperCase() + normalized.slice(1).replace(/[_-]+/g, ' ')
  )
}

export function buildComposioAuthSetupCard(params: {
  toolkitSlug: string
  connectionUrl?: string | null
  connected?: boolean
}): ComposioAuthSetupCard | null {
  const toolkitSlug = params.toolkitSlug.trim().toLowerCase()
  if (!toolkitSlug) return null
  if (params.connected) return null

  const name = getComposioToolkitDisplayName(toolkitSlug)
  const connectionUrl =
    typeof params.connectionUrl === 'string' && params.connectionUrl.trim()
      ? params.connectionUrl.trim()
      : null

  return {
    kind: 'composio_auth',
    title: `Connect ${name}`,
    description: `Losi needs access to ${name} before it can run actions for you. Connect once — then ask again.`,
    toolkit_slug: toolkitSlug,
    connection_url: connectionUrl,
    action_label: 'Connect',
    logo: `https://logos.composio.dev/api/${toolkitSlug}`
  }
}

export function getComposioAuthSetupCard(value: unknown): ComposioAuthSetupCard | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null
  }

  const root = value as Record<string, unknown>
  const setup =
    typeof root.setupCard === 'object' && root.setupCard !== null && !Array.isArray(root.setupCard)
      ? (root.setupCard as Record<string, unknown>)
      : typeof root.setup_card === 'object' &&
          root.setup_card !== null &&
          !Array.isArray(root.setup_card)
        ? (root.setup_card as Record<string, unknown>)
        : root

  const kind = typeof setup.kind === 'string' ? setup.kind.trim().toLowerCase() : ''
  if (kind && kind !== 'composio_auth') {
    return null
  }

  const toolkitSlug =
    (typeof setup.toolkit_slug === 'string' && setup.toolkit_slug.trim()) ||
    (typeof setup.toolkitSlug === 'string' && setup.toolkitSlug.trim()) ||
    (typeof root.toolkit_slug === 'string' && root.toolkit_slug.trim()) ||
    (typeof root.toolkitSlug === 'string' && root.toolkitSlug.trim()) ||
    ''

  if (!toolkitSlug) {
    return null
  }

  // Require an explicit composio_auth kind OR a connection_url with toolkit —
  // avoids treating every ConnectedServices payload as an auth card.
  const connectionUrl =
    (typeof setup.connection_url === 'string' && setup.connection_url.trim()) ||
    (typeof setup.connectionUrl === 'string' && setup.connectionUrl.trim()) ||
    (typeof root.connection_url === 'string' && root.connection_url.trim()) ||
    null

  if (kind !== 'composio_auth' && !connectionUrl) {
    return null
  }

  const name = getComposioToolkitDisplayName(toolkitSlug)
  return {
    kind: 'composio_auth',
    title:
      (typeof setup.title === 'string' && setup.title.trim()) ||
      `Connect ${name}`,
    description:
      (typeof setup.description === 'string' && setup.description.trim()) ||
      `Losi needs access to ${name} before it can run actions for you.`,
    toolkit_slug: toolkitSlug.toLowerCase(),
    connection_url: connectionUrl,
    action_label:
      (typeof setup.action_label === 'string' && setup.action_label.trim()) ||
      (typeof setup.actionLabel === 'string' && setup.actionLabel.trim()) ||
      'Connect',
    logo:
      (typeof setup.logo === 'string' && setup.logo.trim()) ||
      `https://logos.composio.dev/api/${toolkitSlug.toLowerCase()}`
  }
}
